import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate,
} from 'react-router-dom';
import AppLayout from '@/layouts/app';
import PublicLayout from '@/layouts/public';
import HomePage from '@/pages/home';
import PublicForm from '@/pages/public/form';
import ProtectedRoute from './auth/protected-route';
import ErrorBoundary from './error-boundary';
import ContactsPage from './pages/app/contacts';
import Dashboard from './pages/app/dashboard';
import DatabasesPage from './pages/app/databases';
import DealsPage from './pages/app/deals';
import DealDetailsPage from './pages/app/deals/[id]';
import DomainsPage from './pages/app/domains';
import DomainDetailsPage from './pages/app/domains/[name]';
import FdicMonitorPage from './pages/app/fdic-monitor';
import MessagesPage from './pages/app/messages';
import MonitorPage from './pages/app/monitor';
import DetailedScansPage from './pages/app/monitor/detailed-scans';
import MonitorEventsPage from './pages/app/monitor/events';
import RddsSearchPage from './pages/app/monitor/rdds-search';
import PartnersPage from './pages/app/partners';
import RawChangesPage from './pages/app/raw-changes';
import RawTransactionsPage from './pages/app/raw-transactions';
import ReportsPage from './pages/app/reports';
import BankCumulativeReport from './pages/app/reports/bank-cumulative';
import BatchCalendarReport from './pages/app/reports/batch-calendar';
import ContactsByDomainStatusReport from './pages/app/reports/contacts-by-domain-status';
import DealsMissingOutcomeReport from './pages/app/reports/deals-missing-outcome';
import DealsWonLostReport from './pages/app/reports/deals-won-lost';
import FdicLicensesReport from './pages/app/reports/fdic-licenses';
import MarketingContactsReport from './pages/app/reports/marketing-contacts';
import ReportBuilderPage from './pages/app/reports/report-builder';
import StateAdoptionReport from './pages/app/reports/state-adoption';
import SchedulerPage from './pages/app/scheduler';
import SchedulePage from './pages/app/scheduler/schedule';
import SettingsPage from './pages/app/settings';
import TasksPage from './pages/app/tasks';
import TemplatesPage from './pages/app/templates';
import ZonesPage from './pages/app/zones';
import LoginPage from './pages/login';
import Logout from './pages/logout';
import NotFound from './pages/not-found';
import SignupPage from './pages/signup';

const routeObject = createRoutesFromElements(
  <Route>
    <Route path="/" element={<PublicLayout />} errorElement={<ErrorBoundary />}>
      <Route index element={<HomePage />} />
      <Route path="/login/*" element={<LoginPage />} />
      <Route path="/signup/*" element={<SignupPage />} />
      <Route path="*" element={<NotFound />} />
    </Route>

    {/* Standalone form route without layout */}
    <Route path="/form" element={<PublicForm />} errorElement={<ErrorBoundary />} />

    <Route path="/app" element={<ProtectedRoute component={AppLayout} />} errorElement={<ErrorBoundary />}>
      <Route index element={<Navigate to="/app/dashboard" replace />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="deals" element={<DealsPage />} />
      <Route path="deals/:dealId" element={<DealDetailsPage />} />
      <Route path="domains" element={<DomainsPage />} />
      <Route path="domains/:name" element={<DomainDetailsPage />} />
      <Route path="zones" element={<ZonesPage />} />
      <Route path="monitor" element={<MonitorPage />} />
      <Route path="monitor/events" element={<MonitorEventsPage />} />
      <Route path="monitor/detailed-scans" element={<DetailedScansPage />} />
      <Route path="monitor/rdds-search" element={<RddsSearchPage />} />
      <Route path="fdic-monitor" element={<FdicMonitorPage />} />
      <Route path="raw-transactions" element={<RawTransactionsPage />} />
      <Route path="raw-changes" element={<RawChangesPage />} />
      <Route path="databases" element={<DatabasesPage />} />
      <Route path="partners" element={<PartnersPage />} />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="templates" element={<TemplatesPage />} />
      <Route path="contacts" element={<ContactsPage />} />
      <Route path="messages" element={<MessagesPage />} />
      <Route path="scheduler" element={<SchedulerPage />} />
      <Route path="scheduler/schedule" element={<SchedulePage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="reports/bank-cumulative" element={<BankCumulativeReport />} />
      <Route path="reports/batch-calendar" element={<BatchCalendarReport />} />
      <Route path="reports/contacts-by-domain-status" element={<ContactsByDomainStatusReport />} />
      <Route path="reports/deals-missing-outcome" element={<DealsMissingOutcomeReport />} />
      <Route path="reports/deals-won-lost" element={<DealsWonLostReport />} />
      <Route path="reports/fdic-licenses" element={<FdicLicensesReport />} />
      <Route path="reports/marketing-contacts" element={<MarketingContactsReport />} />
      <Route path="reports/report-builder" element={<ReportBuilderPage />} />
      <Route path="reports/state-adoption" element={<StateAdoptionReport />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="*" element={<NotFound />} />
    </Route>

    <Route path="/logout" element={<Logout />} errorElement={<ErrorBoundary />} />
  </Route>,
);

const router = createBrowserRouter(routeObject);

export default router;
